<template>
    <div>
        <h1 class="font-bold text-4xl mb-20">
            <span class="emoji">☕️</span> Digital Nomad friendly cafes
            <template v-if="city">
                in {{ city.emoji }} {{ city.name }},
                {{ country.emoji }} {{ country.name }}
            </template>
        </h1>
        <template v-if="!preview">
            <p v-if="city" class="mb-10">
                Welcome to Digital Nomad Cafes - your ultimate guide to the best
                laptop-friendly cafes in {{ city.name }}.
            </p>
            <p v-else class="mb-10">
                Welcome to Digital Nomad Cafes - your ultimate guide to the best
                laptop-friendly cafes.
            </p>
            <p v-if="city" class="mb-10">
                As a digital nomad, freelancer, or professional, you need a comfortable and productive place to work.
                That's why I've crowdsourced a list of the top cafes in {{ city.name }} that cater to your needs.
                With comfortable seating, fast Wi-Fi, and ample power outlets, these cafes provide the perfect
                environment
                to work, collaborate, explore the city and get things done.
            </p>
            <p v-else class="mb-10">
                As a digital nomad, freelancer, or professional, you need a comfortable and productive place to work.
                That's why I've crowdsourced a list of the top cafes that cater to your needs.
                With comfortable seating, fast Wi-Fi, and ample power outlets, these cafes provide the perfect
                environment
                to work, collaborate, explore the city and get things done.
            </p>

            <p class="mb-10">
                Our cafe listings include detailed information and user reviews, such as types of seating, Wi-Fi speeds,
                and
                power outlets availability, so you can find the perfect spot for your needs.
                Whether you're looking for a trendy spot or a quiet corner, we've got you covered.
            </p>

            <p class="">
                Look for cafes which are marked as <span class="text-indigo-400 font-bold">(recently) verified</span>,
                as this means that a verified digital nomad has visited the cafe
                and confirmed whether it's friendly for digital nomads to work in. These places are generally the best
                cafes to work in as a digital nomad if you don't
                want to worry about anything.
            </p>

            <p v-if="citySlug"
               class="mt-10">Do you know more spots that can be added to this list?
                <router-link :to="`/account/cafes/add/${countrySlug}/${citySlug}`"
                             class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">Add them
                    yourself
                </router-link>
                to
                help out
                your fellow digital nomads
                and remote
                workers!
            </p>
        </template>

        <div
            :class="{ 'bg-gray-100': loading }"
            class="mt-20 h-[300px] relative "
        >
            <l-map v-if="city"
                   ref="map"
                   v-model:zoom="zoom"
                   :center="[city.latitude, city.longitude]"
                   :max-zoom="19"
                   :options="{attributionControl: false}"
                   fade-animation>
                <l-tile-layer
                    attribution="© <a target='_blank' href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                    layer-type="base"
                    name="OpenStreetMap"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                ></l-tile-layer>

                <l-control-attribution
                    position="bottomright"
                    prefix=""
                />

                <l-marker v-for="cafe in city.workplaces"
                          :lat-lng="[cafe.latitude, cafe.longitude]"
                          :name='cafe.name'
                >
                    <l-popup>
                        <router-link :to="cafe.url"
                                     class="text-blue-400 underline">
                            {{ cafe.name }}
                        </router-link>
                    </l-popup>
                </l-marker>
            </l-map>
        </div>

        <WorkplaceGrid
            v-if="city && city?.workplaces.length > 0"
            :cafes="city.workplaces"
        />

        <div v-else-if="city?.workplaces.length === 0"
             class="mt-20 rounded overflow-hidden border-1 border-gray-200 shadow bg-white p-15 max-w-2xl mx-auto">
            <p class="mb-10">
                Unfortunately, we do not currently have any information on digital nomad-friendly cafes in
                {{ city.name }}.
                However, we encourage you to add your own cafes and workplaces to our directory so other digital
                nomads and professionals can benefit from your recommendations.
            </p>

            <p class="mb-10">
                Help us create the ultimate guide for working remotely and add your favorite cafe today!
            </p>

            <p class="text-center mt-20">
                <router-link :to="`/account/cafes/add/${countrySlug}/${citySlug}`"
                             class="rounded py-5 px-10 border-1 border-indigo-400 transition-all text-indigo-400 hover:border-pink-500 hover:text-pink-500 focus:text-pink-500">
                    Add workplace
                </router-link>
            </p>
        </div>

        <p v-if="city" class="mb-10 mt-20">Know a place?
            <router-link :to="`/account/cafes/add/${country.slug}/${city.slug}`"
                         class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">
                Add a new cafe or remote
                working spot
            </router-link>
            to help out your fellow digital nomads and
            remote workers!
        </p>

        <div v-if="city">
            <p class="mb-10">
                When you're working on the go, it's important to have a good meal or a coffee to fuel your productivity.
                And when you're digital nomading, you have to put in some hours but also want to explore the city.
                Working from cafes is an ideal solution to getting work done and still discovering new places and
                getting around {{ city.name }}.
            </p>

            <p class="mb-10">
                Don't waste your time searching for the perfect spot when others have already done the work for you.
                Browse our list of top-rated
                <router-link :to="`/cafes/${country.slug}/${city.slug}`"
                             class="text-indigo-400 underline hover:text-pink-500 focus:text-pink-500">
                    laptop-friendly cafes in {{ city.name }}
                </router-link>
                and find the perfect place to
                work, relax, and connect.
            </p>

            <p class="mb-10">
                Bookmark Digital Nomad Cafes and make it your go-to guide for finding the best cafes to work from in
                {{ city.name }}.
            </p>
        </div>
    </div>
</template>

<script>
import "leaflet/dist/leaflet.css"

import {useMeta} from "vue-meta";
import {LControlAttribution, LMap, LMarker, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import WorkplaceGrid from "../components/WorkplaceGrid";
import axios from "axios";
import {computed} from "vue";

export default {
    name: "City",
    components: {WorkplaceGrid, LMap, LTileLayer, LMarker, LPopup, LControlAttribution},
    props: {
        countrySlug: String,
        citySlug: String
    },
    data() {
        return {
            zoom: 12.5,
            country: {},
            city: null,
            loading: false,
            preview: this.$route.query.preview
        }
    },
    methods: {
        getCity() {
            this.loading = true;

            axios.get(`/api/countries/${this.countrySlug}/cities/${this.citySlug}`).then(response => {
                this.city = response.data.city;
                this.country = response.data.country;

                useMeta({
                    title: `Digital Nomad Cafes in ${this.country.emoji} ${this.city.name}`
                });
            }).catch(error => {
                this.errors = error.response?.data?.errors ?? {};
            }).finally(() => {
                this.loading = false;
            });
        },
    },
    mounted() {
        useMeta(
            computed(() => {
                if (this.city) {
                    return {
                        title: `Digital Nomad Cafes in ${this.country.emoji} ${this.city.name}`,
                        description: `View remote worker friendly cafes in ${this.city.name} on digitalnomadcafes.com.
                        Browse through more than ${this.city.workplaces.length} workplaces where you can put down your laptop and get to work in ${this.city.name}!
                        Most of our cafes have been verified by our users, guaranteeing you that the information is up to date and accurate!`,
                        og: {
                            title: `Digital Nomad Cafes in ${this.country.emoji} ${this.city.name}`,
                            image: `/api/countries/${this.country.slug}/cities/${this.city.slug}/image`,
                            description: `View remote worker friendly cafes in ${this.city.name} on digitalnomadcafes.com.
                        Browse through more than ${this.city.workplaces.length} workplaces where you can put down your laptop and get to work in ${this.city.name}!
                        Most of our cafes have been verified by our users, guaranteeing you that the information is up to date and accurate!`
                        }
                    };
                }

                return {};
            })
        );
    },
    created() {
        this.getCity();
    }
}
</script>
